import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { User, Session, AuthError, AuthResponse, AuthTokenResponsePassword, AuthOtpResponse } from '@supabase/supabase-js';
import { supabase, supabaseInitError } from '@/integrations/supabase/client';
import { identifyUser, clearUser } from '@/lib/error-tracking';
import { setSignedInCookie, clearSignedInCookie } from '@/lib/session-cookie';
import { IMPERSONATION_KEY, hasStoredImpersonation, endImpersonation } from '@/lib/impersonation';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthTokenResponsePassword>;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  /** `isImpersonating` true means the admin session was restored instead of signing out (#2836);
   *  endImpersonation already navigated, so callers must not redirect. */
  signOut: () => Promise<{ error: AuthError | null; isImpersonating: boolean }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Stub provider used when Supabase env vars are missing (e.g. preview deploys) */
function AuthProviderStub({ children }: { children: ReactNode }) {
  const stubError = new AuthError(supabaseInitError!);
  return (
    <AuthContext.Provider value={{
      user: null,
      session: null,
      loading: false,
      signIn: async () => ({ data: { user: null, session: null }, error: stubError }),
      signInWithMagicLink: async () => ({ error: stubError }),
      signOut: async () => ({ error: stubError, isImpersonating: false }),
      resetPassword: async () => ({ error: stubError }),
      updatePassword: async () => ({ error: stubError }),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthProviderInner({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
      if (session?.user) { identifyUser(session.user.id, session.user.email ?? undefined); } else { clearUser(); }
        // Mirror the session into the cross-domain flag cookie so the
        // marketing nav can show "My dashboard" (#2590). Fires on sign-in,
        // token refresh (extends the 30d window), and sign-out.
        if (session) { setSignedInCookie(); } else { clearSignedInCookie(); }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) { identifyUser(session.user.id, session.user.email ?? undefined); } else { clearUser(); }
      // Session restore on page load — keep the flag cookie in sync (#2590).
      if (session) { setSignedInCookie(); } else { clearSignedInCookie(); }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      // Land on the app root so the role-aware home arbitrates the surface:
      // buyer-type sessions → the desk at /dashboard, staff → the classic
      // dashboard (#2530, RoleDashboard).
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    // While impersonating, "sign out" must return the operator to their own session rather
    // than end it. The stashed admin tokens live under this key and are the ONLY way back;
    // clearing them and signing out stranded the admin at /auth with no recovery (#2836).
    // endImpersonation navigates on its own, so callers' post-signOut redirects are moot —
    // hence the isImpersonating flag in the return value for callers that branch on it.
    if (hasStoredImpersonation()) {
      await endImpersonation();
      return { error: null, isImpersonating: true };
    }
    // Real sign-out: clear any stale stash so a banner can't survive into the next login.
    sessionStorage.removeItem(IMPERSONATION_KEY);
    const { error } = await supabase.auth.signOut();
    return { error, isImpersonating: false };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signInWithMagicLink,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (supabaseInitError) return <AuthProviderStub>{children}</AuthProviderStub>;
  return <AuthProviderInner>{children}</AuthProviderInner>;
}
